import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { Redirect, Switch, useLocation, useRouteMatch } from 'react-router';
import { AppState } from '../../store/appState';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EMAGAZINES_OBJECT,
    LOANFORMAT_EPRESS_OBJECT,
    URL_LOANFORMAT_ALL,
    URL_LOANFORMAT_EAUDIOBOOKS,
    URL_LOANFORMAT_EBOOKS,
    URL_LOANFORMAT_EPRESS
} from '../../store/loanFormat/actions';
import { PageNotFound } from '../../shop/PageNotFound';
import { CompleteCollectionPage } from './complete/CompleteCollectionPage';
import { CheckLoanFormat } from '../../shared/components/CheckLoanFormat';
import ReserveReportList from '../../shop/reserve/ReserveReportList';
import { ReserveDetail } from '../../shop/reserve/ReserveDetail';
import { ReserveCheckout } from '../../shop/reserve/ReserveCheckout';
import { ManageSubscriptionsPage } from '../../shop/subscriptions/ManageSubscriptionsPage';
import { ManageRenewalsPage } from '../../shop/renewals/ManageRenewalsPage';
import { YourCampaignsPage } from '../marketing/campaigns/YourCampaignsPage';
import { CAMPAIGN_PRODUCTS, useFeature } from '../../shared/utils/togglz';
import { YourTitlesPage } from '../localcontent/yourtitles/YourTitlesPage';
import { LCProductDetailNewPage } from '../localcontent/product/create/LCProductDetailNewPage';
import { CheckRequiredSpoc, SpocRequiredFor } from '../../shared/common/CheckRequiredSpoc/CheckRequiredSpoc';
import { LCProductDetailReadPage } from '../localcontent/product/read/LCProductDetailReadPage';
import { LCProductDetailEditPage } from '../localcontent/product/edit/LCProductDetailEditPage';
import { PerformersPage } from '../localcontent/performer/PerformersPage';
import { LocalContentCollectionPage } from '../localcontent/collection/LocalContentCollectionPage';
import { CollectionSearchResultPage } from '../../shared/search/CollectionSearchResultPage';
import { ProductSelectionProvider } from '../../shared/product-selection/ProductSelectionContext';

const CollectionManagementRouteSwitch: FC = () => {
    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const { path, url } = useRouteMatch();
    const { search } = useLocation();
    const currentLoanFormat = useSelector((state: AppState) => {
        switch (state.loanFormat.key) {
            case LOANFORMAT_EBOOK_OBJECT.key:
                return URL_LOANFORMAT_EBOOKS;
            case LOANFORMAT_EAUDIOBOOK_OBJECT.key:
                return URL_LOANFORMAT_EAUDIOBOOKS;
            case LOANFORMAT_EPRESS_OBJECT.key:
            case LOANFORMAT_EMAGAZINES_OBJECT.key:
                return URL_LOANFORMAT_EPRESS;
            default:
                return URL_LOANFORMAT_ALL;
        }
    });

    return (
        <Switch>
            <Route exact path={path}>
                <Redirect to={{ pathname: `${url}/collection`, search }} />
            </Route>
            <Route exact path={`${path}/collection`}>
                <Redirect to={{ pathname: `${url}/collection/${currentLoanFormat}`, search }} />
            </Route>
            <Route exact path={`${path}/collection/:loanFormat`}>
                <CheckLoanFormat>
                    <ProductSelectionProvider>
                        <CompleteCollectionPage />
                    </ProductSelectionProvider>
                </CheckLoanFormat>
            </Route>

            <Route exact path={`${path}/reserves`}>
                <ReserveReportList />
            </Route>
            <Route exact path={`${path}/reserves/:reserveListId`}>
                <ReserveDetail />
            </Route>
            <Route exact path={`${path}/reserves/:reserveListId/checkout`}>
                <ReserveCheckout />
            </Route>

            {isCampaignsEnabled ? (
                <Route exact path={`${path}/campaigns`}>
                    <Redirect
                        to={{
                            pathname: `${url}/campaigns/${
                                currentLoanFormat === URL_LOANFORMAT_EPRESS ? URL_LOANFORMAT_ALL : currentLoanFormat
                            }`,
                            search
                        }}
                    />
                </Route>
            ) : null}
            {isCampaignsEnabled ? (
                <Route exact path={`${path}/campaigns/:loanFormat`}>
                    <CheckLoanFormat>
                        <YourCampaignsPage />
                    </CheckLoanFormat>
                </Route>
            ) : null}

            <Route exact path={`${path}/subscriptions`}>
                <ManageSubscriptionsPage />
            </Route>
            <Route exact path={`${path}/subscriptions/renewals`}>
                <ManageRenewalsPage />
            </Route>

            <Route exact path={`${path}/localcontent`}>
                <Redirect to={{ pathname: `${url}/localcontent/collection`, search }} />
            </Route>
            <Route exact path={`${path}/localcontent/products`}>
                <YourTitlesPage />
            </Route>
            <Route exact path={`${path}/localcontent/products/new`}>
                <LCProductDetailNewPage />
            </Route>
            <Route exact path={`${path}/localcontent/products/:id`}>
                <CheckRequiredSpoc type={SpocRequiredFor.LOCAL_CONTENT_PRODUCT}>
                    <LCProductDetailReadPage />
                </CheckRequiredSpoc>
            </Route>
            <Route exact path={`${path}/localcontent/products/:id/edit`}>
                <CheckRequiredSpoc type={SpocRequiredFor.LOCAL_CONTENT_PRODUCT}>
                    <LCProductDetailEditPage />
                </CheckRequiredSpoc>
            </Route>
            <Route exact path={`${path}/localcontent/performers`}>
                <PerformersPage />
            </Route>
            <Route exact path={`${path}/localcontent/collection`}>
                <LocalContentCollectionPage />
            </Route>

            <Route exact path={`${path}/search/:loanFormat`}>
                <CheckLoanFormat>
                    <ProductSelectionProvider>
                        <CollectionSearchResultPage />
                    </ProductSelectionProvider>
                </CheckLoanFormat>
            </Route>

            <Route>
                <PageNotFound />
            </Route>
        </Switch>
    );
};

export default CollectionManagementRouteSwitch;
